import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { hasRole, isReadOnlyGuest } from "@/lib/roles";
import { Sparkles, LayoutDashboard, Users, Package, Building2, MapPin, Calendar, BellRing, BarChart3, ShoppingCart, LogOut, ExternalLink, Truck, GitMerge, ScaleIcon, ClipboardList, UserCog, Store, Menu, X, Zap, BadgePercent, CircleUserRound, ChevronDown, Star, Recycle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    if (user?.role !== "personnel" || !navigator.geolocation) return;
    const send = () => navigator.geolocation.getCurrentPosition(
      (pos) => api.post(`/personnel/${user.id}/location`, { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {}),
      () => {}, { enableHighAccuracy: true, timeout: 8000 }
    );
    send();
    const t = setInterval(send, 30000);
    return () => clearInterval(t);
  }, [user]);

  // Close the mobile drawer on route change so tapping a link doesn't leave
  // the menu covering the page.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => { if (user?.id) api.get("/me/sidebar-favorites").then((r) => setFavorites(r.data.favorites || [])).catch(() => {}); }, [user?.id]);

  const links = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "personnel", "client", "distributing_partner"] },
    { to: "/profile", label: "Profile", icon: CircleUserRound, roles: ["admin", "personnel", "client", "distributing_partner"] },
    { to: "/recycle-bin", label: "Recycle Bin", icon: Recycle, roles: ["admin", "personnel", "distributing_partner"] },
    { to: "/clients", label: "Clients", icon: Users, roles: ["admin", "personnel", "distributing_partner"], group: "People" },
    { to: "/orders", label: "Client Orders", icon: ShoppingCart, roles: ["admin", "personnel", "client", "distributing_partner"], group: "Commerce" },
    { to: "/inventory", label: "Inventory", icon: Package, roles: ["admin", "personnel", "distributing_partner"], group: "Commerce" },
    { to: "/vendors", label: "Vendors", icon: Building2, roles: ["admin"], group: "Commerce" },
    { to: "/partners", label: "Distributing Partners", icon: Truck, roles: ["admin"], group: "People" },
    { to: "/ep-applications", label: "DP Applications", icon: ClipboardList, roles: ["admin"], group: "People" },
    { to: "/supply-chain", label: "Supply Chain", icon: Package, roles: ["admin"], group: "Operations" },
    { to: "/purchase-orders", label: "Purchase Orders", icon: ClipboardList, roles: ["admin", "personnel"], group: "Commerce" },
    { to: "/reconcile", label: "CSV Reconcile", icon: GitMerge, roles: ["admin"], group: "Operations" },
    { to: "/reconciliation", label: "Stock Reconciliation", icon: ScaleIcon, roles: ["admin"], group: "Operations" },
    { to: "/chat", label: "Chat", icon: BellRing, roles: ["admin", "personnel", "distributing_partner"], group: "Engage" },
    { to: "/field", label: "Field Map", icon: MapPin, roles: ["admin"], group: "Operations" },
    { to: "/calendar", label: "Calendar", icon: Calendar, roles: ["admin", "personnel", "distributing_partner"], group: "Engage" },
    { to: "/analytics", label: "Analytics", icon: BarChart3, roles: ["admin"], group: "Insights" },
    { to: "/team", label: "Team Access", icon: UserCog, roles: ["admin"], group: "People" },
    { to: "/stripe-events", label: "Stripe Events", icon: Zap, roles: ["admin"], group: "Insights" },
    { to: "/discounts", label: "Discounts", icon: BadgePercent, roles: ["admin"], group: "Commerce" },
    { to: "/licenses", label: "License Management", icon: ClipboardList, roles: ["admin"], group: "People" },
    { to: "/shop", label: "The Store", icon: Store, roles: ["admin", "personnel", "client", "distributing_partner"], group: "Commerce" },
  ];

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const readOnlyGuest = isReadOnlyGuest(user);
  const allowed = links.filter((l) => hasRole(user, l.roles)); const groups = ["People", "Commerce", "Operations", "Engage", "Insights"];
  const toggleFavorite = async (to) => { if (readOnlyGuest) return; const next = favorites.includes(to) ? favorites.filter((x) => x !== to) : [...favorites, to]; setFavorites(next); try { await api.put("/me/sidebar-favorites", { favorites: next }); } catch { setFavorites(favorites); } };

  return (
    <div className="h-screen w-screen flex overflow-hidden bg-[var(--bg)]">
      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[80] lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        className={`w-64 lg:w-60 sidebar-dark flex flex-col h-full shrink-0
                    fixed inset-y-0 left-0 z-[90] transform transition-transform duration-200
                    lg:static lg:z-auto lg:translate-x-0
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="px-4 py-4 border-b border-white/10">
          <div className="flex items-center gap-3 min-w-0">
            <NavLink to="/login" className="shrink-0 hover:opacity-85 transition-opacity" data-testid="sidebar-logo-link">
              <img src="/brand/mpire-logo.png" alt="MPIRE Distribution" className="w-12 h-12 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]" />
            </NavLink>
            <div className="min-w-0 flex flex-col justify-center leading-none">
              <NavLink to="/login" className="font-bold tracking-tight text-white text-[15px] hover:text-white/85 transition-colors" data-testid="app-name">MPower!</NavLink>
              <a href="https://2conmedia.com" target="_blank" rel="noopener noreferrer" className="mt-1.5 block whitespace-nowrap text-[8px] uppercase tracking-[0.16em] text-gray-400 hover:text-amber-400 hover:underline underline-offset-2" data-testid="sidebar-powered-by-link">BY 2CONMEDIA.COM</a>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {allowed.filter((l) => !l.group).map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/"}
                     className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                     data-testid={`sidebar-${label.toLowerCase().replace(/[^a-z]+/g, '-')}-link`}>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
          {favorites.length > 0 && <div className="pt-2"><p className="px-3 py-2 text-[10px] font-bold uppercase text-amber-300">Favs</p>{allowed.filter((l) => favorites.includes(l.to)).map(({to,label,icon:Icon}) => <NavLink key={to} to={to} className="sidebar-link min-h-11"><Icon size={16}/>{label}</NavLink>)}</div>}
          {groups.map((group) => { const items=allowed.filter((l)=>l.group===group); if(!items.length)return null; const expanded=openGroups[group] ?? items.some((l)=>location.pathname===l.to); return <div key={group} className="pt-2"><button onClick={()=>setOpenGroups({...openGroups,[group]:!expanded})} className="w-full min-h-11 px-3 flex items-center justify-between text-xs font-bold uppercase text-gray-300" data-testid={`sidebar-group-${group.toLowerCase()}-toggle`}>{group}<ChevronDown size={16} className={expanded?"rotate-180 transition-transform":"transition-transform"}/></button>{expanded&&items.map(({to,label,icon:Icon})=><div key={to} className="flex items-center"><NavLink to={to} className={({isActive})=>`sidebar-link min-h-11 flex-1 ${isActive?"active":""}`} data-testid={`sidebar-${label.toLowerCase().replace(/[^a-z]+/g,"-")}-link`}><Icon size={16}/>{label}</NavLink>{!readOnlyGuest && <button onClick={()=>toggleFavorite(to)} className="p-3 text-amber-300" aria-label={`Favorite ${label}`} data-testid={`favorite-${label.toLowerCase().replace(/[^a-z]+/g,"-")}`}><Star size={15} fill={favorites.includes(to)?"currentColor":"none"}/></button>}</div>)}</div>; })}
        </nav>
        <div className="border-t border-white/10 p-3 space-y-2">
          <NavLink to="/order" className="sidebar-link" data-testid="open-public-order-link">
            <ExternalLink size={16} /> Warehouse Club
          </NavLink>
          <NavLink to="/essence" className="sidebar-link" data-testid="open-essence-link">
            <Sparkles size={16} /> The Essence of MPire
          </NavLink>
          <div className="px-3 py-2 text-xs text-gray-400">
            <div className="font-semibold text-white" data-testid="user-name">{user?.name}</div>
            <div className="capitalize">{user?.role}</div>
          </div>
          <Button variant="outline" className="w-full justify-start bg-transparent text-white border-white/20 hover:bg-white/10 hover:text-white" onClick={handleLogout} data-testid="logout-button">
            <LogOut size={14} className="mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 h-screen overflow-y-auto overflow-x-hidden">
        {/* Mobile top bar — only shows on screens smaller than lg */}
        <div className="lg:hidden sticky top-0 z-20 bg-[var(--bg)] border-b border-[var(--border)] flex items-center justify-between px-2 py-1.5">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="p-1.5 rounded-md hover:bg-black/5 active:bg-black/10"
            aria-label="Open menu"
            data-testid="mobile-menu-button"
          >
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-1.5">
            <img src="/brand/mpire-logo.png" alt="MPIRE" className="w-6 h-6 object-contain" />
            <span className="font-bold tracking-tight text-sm">MPower!</span>
          </div>
          <div className="w-7" />
        </div>
        <Outlet />
      </main>
    </div>
  );
}
